<template>
  <div class="login-page">
    <Header />

    <FlashMessage ref="flashMessage" />

    <div class="login-image d-none d-lg-block">
      <img src="@/assets/img/default/login.png" alt="Login Image" />
    </div>

    <div class="login-form">
      <div class="form-container">
        <h2>Account Recovery</h2>
        <p>Please provide your Email</p>

        <LoadingIndicator v-if="loading" message="Loading..." />

      <form @submit.prevent="
        step === 1 ? HandleOTPRequest()
        : step === 2 ? HandleOTPverification()
        : HandlePasswordReset()
        ">

        <!-- EMAIL -->
        <div class="form-group">
            <label>Email</label>
            <input
            type="email"
            v-model="email"
            :disabled="step > 1"
            required
            />
        </div>

        <!-- OTP INFO (MESSAGE + TIMER) -->
        <div v-if="step === 2" class="otp-info">
            <p class="success-text">
            {{ otpMessage }}
            </p>

            <p class="timer-text" v-if="countdown > 0">
            OTP expires in: <strong>{{ formatTime(countdown) }}</strong>
            </p>

            <p v-else class="error-text">
            OTP expired. Please request a new one.
            </p>
        </div>

        <!-- OTP INPUT -->
        <div v-if="step === 2" class="form-group">
            <label>OTP</label>
            <input
            type="text"
            v-model="otp"
            placeholder="Enter OTP"
            :disabled="countdown === 0"
            required
            />
        </div>

        <!-- RESEND OTP -->
        <p
            v-if="step === 2 && countdown === 0"
            class="resend-link"
            @click="HandleOTPRequest"
        >
            Resend OTP
        </p>

        <!-- PASSWORD INPUTS -->
        <div v-if="step === 3">
            <div class="form-group">
            <label>New Password</label>
            <input
                type="password"
                v-model="password"
                required
            />
            </div>

            <div class="form-group">
            <label>Confirm Password</label>
            <input
                type="password"
                v-model="confirmPassword"
                required
            />
            </div>
        </div>

        <!-- BUTTON -->
        <button
            type="submit"
            class="login-button"
            :disabled="loading || (step === 2 && countdown === 0)"
        >
            {{
            step === 1 ? "Send OTP" :
            step === 2 ? "Verify OTP" :
            "Reset Password"
            }}
        </button>

        </form>

        <p class="signup-link">
          Don't have an account?
          <router-link to="/signup">Sign Up</router-link>
        </p>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, watch } from "vue";
import Header from "@/components/Header.vue";
import FlashMessage from "@/components/FlashMessage.vue";
import LoadingIndicator from "@/components/Loading_indicator.vue";

import {
  RequestPasswordResetOTP,
  VerifyPasswordResetOTP,
  ResetPassword
} from "../../API-requests/Requests";
import { useRouter } from "vue-router";

const router = useRouter();

const email = ref("");
const otp = ref("");
const password = ref("");
const confirmPassword = ref("");

const resetToken = ref(null);

const step = ref(1);
const loading = ref(false);
const flashMessage = ref(null);

const otpMessage = ref("");
const countdown = ref(300);
let timer = null;

// STEP 1: Request OTP
const HandleOTPRequest = async () => {
  loading.value = true;

  try {
    const resp = await RequestPasswordResetOTP({
      email: email.value
    });

    otpMessage.value = resp.message;

    startCountdown();


    flashMessage.value.showFlashMessage(
      "success",
      "OTP sent successfully"
    );

    step.value = 2; // move to OTP step

  } catch (err) {
    flashMessage.value.showFlashMessage(
      "failure",
      err.message
    );
  } finally {
    loading.value = false;
  }
};

// STEP 2: Verify OTP
const HandleOTPverification = async () => {
  loading.value = true;

  try {
    const resp = await VerifyPasswordResetOTP({
      email: email.value,
      otp: otp.value
    });

    resetToken.value = resp.data.reset_token;

    flashMessage.value.showFlashMessage(
      "success",
      "OTP verified successfully"
    );

    step.value = 3;

  } catch (err) {
    flashMessage.value.showFlashMessage(
      "failure",
      err.message
    );
  } finally {
    loading.value = false;
  }
};

// STEP 3: Reset Password
const HandlePasswordReset = async () => {
  if (password.value.length < 6) {
    return flashMessage.value.showFlashMessage(
      "failure",
      "Password must be at least 6 characters"
    );
  }

  if (password.value !== confirmPassword.value) {
    return flashMessage.value.showFlashMessage(
      "failure",
      "Passwords do not match"
    );
  }

  loading.value = true;

  try {
    await ResetPassword({
      reset_token: resetToken.value,
      password: password.value,
      password_confirmation: confirmPassword.value
    });

    flashMessage.value.showFlashMessage(
      "success",
      "Password reset successfully"
    );

    // redirect after short delay
    setTimeout(() => {
      router.push("/login");
    }, 1500);

  } catch (err) {
    flashMessage.value.showFlashMessage(
      "failure",
      err.message
    );
  } finally {
    loading.value = false;
  }
};


const startCountdown = () => {
  countdown.value = 300;

  if (timer) clearInterval(timer);

  timer = setInterval(() => {
    if (countdown.value > 0) {
      countdown.value--;
    } else {
      clearInterval(timer);
    }
  }, 1000);
};

const formatTime = (seconds) => {
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return `${min}:${sec < 10 ? "0" : ""}${sec}`;
};

watch(countdown, (val) => {
  if (val === 0) {
    otp.value = "";
  }
});
</script>

<style scoped>
.login-page {
  display: flex;
  min-height: 100vh;
}

.login-image {
  flex: 1;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-image img {
  max-width: 100%;
  height: 100vh;
  object-fit: cover;
}

.login-form {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: #fff;
}

.form-container {
  max-width: 400px;
  width: 100%;
}

h2 {
  font-size: 2rem;
  margin-bottom: 10px;
  color: #333;
}

p {
  font-size: 1rem;
  color: #666;
  margin-bottom: 30px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 0.9rem;
  color: #333;
  margin-bottom: 5px;
}

.form-group input {
  width: 100%;
  padding: 10px;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  outline: none;
}

.form-group input:focus {
  border-color: #42b983;
  box-shadow: 0 0 5px rgba(66, 185, 131, 0.5);
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.form-check {
  display: flex;
  align-items: center;
}

.form-check input {
  margin-right: 10px;
}

.forgot-password {
  font-size: 0.9rem;
  color: #42b983;
  text-decoration: none;
}

.forgot-password:hover {
  text-decoration: underline;
}

.login-button {
  width: 100%;
  padding: 10px;
  font-size: 1rem;
  color: #fff;
  background-color: #42b983;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.login-button:hover {
  background-color: #3aa876;
}

.signup-link {
  font-size: 0.9rem;
  color: #666;
  text-align: center;
  margin-top: 20px;
}

.signup-link a {
  color: #42b983;
  text-decoration: none;
}

.signup-link a:hover {
  text-decoration: underline;
}

@media (max-width: 992px) {
  .login-image {
    display: none;
  }

  .login-form {
    flex: 1;
    padding: 40px 20px;
  }
}









.otp-info {
  margin: 12px 0;
  padding: 10px;
  border-radius: 6px;
  background: #f8f9fa;
}

.success-text {
  color: #28a745;
  font-size: 14px;
  font-weight: 500;
}

.timer-text {
  color: #dc3545;
  font-size: 13px;
  margin-top: 5px;
}

.error-text {
  color: #dc3545;
  font-size: 13px;
  font-weight: 500;
}

.resend-link {
  margin-top: 8px;
  font-size: 13px;
  color: #007bff;
  cursor: pointer;
}

.resend-link:hover {
  text-decoration: underline;
}
</style>
